import {
  FORM_DICTATION_MAX,
  FORM_INSTRUCTIONS_MAX,
  type FormFillMode,
} from "@rosc/contracts";
import { Button } from "@rosc/ui/v32/button";
import { Field, FieldLabel } from "@rosc/ui/v32/field";
import {
  GenerationProgress,
  useTimedStep,
} from "@rosc/ui/v32/generation-progress";
import { InlineNotice } from "@rosc/ui/v32/notice";
import { SegmentedControl } from "@rosc/ui/v32/segmented-control";
import {
  ArrowLeft,
  Check,
  CircleCheck,
  Copy,
  Download,
  FileCheck2,
  Loader2,
  Lock,
  Mic,
  Pencil,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DictatedField } from "@/components/ui/doctor-notes/DictatedField";
import { V3Drawer } from "@/components/ui/v3/drawer";
import {
  type CompletedForm,
  type CompletedFormSummary,
  createFormTemplate,
  downloadVisitForm,
  type FormField,
  type FormTemplate,
  fillVisitForm,
  getVisitForm,
  refusalOf,
  saveFormTemplate,
  updateVisitForm,
  uploadProblem,
  useFormTemplates,
  useVisitForms,
} from "@/lib/form-completion/queries";
import {
  emptyReview,
  type FormReviewState,
  formValuesText,
  pendingKeys,
  reviewBody,
} from "@/lib/form-completion/review";
import { cn } from "@/lib/utils";
import { usePhoneLayout } from "@/pages/Visit/workspace/layout";
import { FormChooser, type FormProblem } from "./FormChooser";
import { FormFieldsEditor } from "./FormFieldsEditor";
import { FormReview } from "./FormReview";

type Step =
  | { kind: "choose" }
  | { kind: "fields"; template: FormTemplate }
  | { kind: "ready"; template: FormTemplate }
  | { kind: "filling"; template: FormTemplate }
  | { kind: "review"; form: CompletedForm }
  | { kind: "done"; form: CompletedForm };

// Filling from the visit, one request with no stages of its own: reading,
// then (after 3 s) filling, which stays current until the values arrive.
const Filling = ({
  title,
  body,
  dictated,
}: {
  title: string;
  body?: string;
  dictated: boolean;
}) => {
  const { t } = useTranslation();
  const step = useTimedStep([3000]);
  return (
    <div className="px-5 pb-6 pt-1">
      <GenerationProgress
        current={step}
        description={body}
        steps={[
          {
            key: "reading",
            label: dictated
              ? t("forms.filling.readingDictation")
              : t("generationProgress.steps.readingVisit"),
          },
          {
            key: "filling",
            label: t("generationProgress.steps.fillingForm"),
          },
        ]}
        testId="form-busy"
        title={title}
      />
    </div>
  );
};

/**
 * AI form completion in a visit (VITE_FORM_COMPLETION_ENABLED): choose a
 * saved form, a starter or upload a blank PDF; confirm its fields once;
 * choose "Use visit information" (ROSC fills the clinical fields from the
 * visit) or "Dictate" (only what the clinician dictates, with instructions,
 * is sent; nothing of the visit); review: uncertain values are marked and
 * checked, every value can be edited, the patient's identifiers are typed;
 * then download the original PDF with the values written in, or copy the
 * values. Nothing is signed.
 */
export const FormCompletionSheet = ({
  open,
  onOpenChange,
  visitPublicId,
  sourceIds,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitPublicId: string;
  // The visit sources selected in the assistant (sent with the fill).
  sourceIds: readonly number[];
}): JSX.Element => {
  const { t } = useTranslation();
  const phone = usePhoneLayout();
  const templates = useFormTemplates({ enabled: open });
  const recent = useVisitForms(visitPublicId, { enabled: open });
  const [step, setStep] = useState<Step>({ kind: "choose" });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<FormProblem | null>(null);
  const [name, setName] = useState("");
  const [fields, setFields] = useState<FormField[]>([]);
  const [review, setReview] = useState<FormReviewState>(emptyReview());
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [mode, setMode] = useState<FormFillMode>("visit");
  const [dictation, setDictation] = useState("");
  const [instructions, setInstructions] = useState("");
  const [touched, setTouched] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const ids = {
    dictation: useId(),
    dictationHint: useId(),
    instructions: useId(),
  };

  useEffect(() => {
    if (!open) return;
    setStep({ kind: "choose" });
    setProblem(null);
    setBusy(false);
    setActiveKey(null);
    setMode("visit");
    setDictation("");
    setInstructions("");
    setTouched(false);
    setCopied(false);
  }, [open]);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const explain = (error: unknown): FormProblem => {
    const refusal = refusalOf(error);
    if (refusal === "xfa" || refusal === "encrypted" || refusal === "notPdf")
      return {
        title: t("forms.errors.unreadable.title"),
        description: t("forms.errors.unreadable.description"),
      };
    if (refusal) return { title: t(`forms.errors.${refusal}`) };
    return { title: t("forms.errors.generic") };
  };

  const openFields = (template: FormTemplate) => {
    setName(
      template.source === "starter"
        ? t("forms.starters.work-absence-note", { defaultValue: template.name })
        : template.name,
    );
    setFields(template.fields);
    setStep({ kind: "fields", template });
  };
  const startReview = (form: CompletedForm) => {
    const next = emptyReview(form);
    setReview(next);
    const pending = pendingKeys(form.entries, next);
    setActiveKey(pending[0] ?? null);
    setStep({ kind: form.state === "completed" ? "done" : "review", form });
  };
  const run = async (task: () => Promise<void>, back?: Step) => {
    setBusy(true);
    setProblem(null);
    try {
      await task();
    } catch (error) {
      setProblem(explain(error));
      if (back) setStep(back);
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File) => {
    const local = uploadProblem(file);
    if (local) {
      setProblem({ title: t(`forms.errors.${local}`) });
      return;
    }
    void run(async () => openFields(await createFormTemplate({ file })));
  };
  const dictationMissing = mode === "dictation" && !dictation.trim();
  const fill = (template: FormTemplate) => {
    setTouched(true);
    if (dictationMissing) {
      document.getElementById(ids.dictation)?.focus();
      return;
    }
    setStep({ kind: "filling", template });
    void run(
      async () => {
        const form = await fillVisitForm(
          visitPublicId,
          template.public_id,
          mode === "dictation"
            ? { source: "dictation", dictation, instructions }
            : { source: "visit", sourceIds },
        );
        await recent.refresh();
        startReview(form);
      },
      { kind: "ready", template },
    );
  };
  const saveFields = (template: FormTemplate) =>
    run(async () => {
      const saved = await saveFormTemplate(template.public_id, {
        name: name.trim() || template.name,
        fields,
      });
      setStep({ kind: "ready", template: saved });
    });
  const complete = (form: CompletedForm) =>
    run(async () => {
      const done = await updateVisitForm(
        form.public_id,
        reviewBody(form, review, true),
      );
      await recent.refresh();
      setReview(emptyReview(done));
      setStep({ kind: "done", form: done });
    });
  const download = (form: CompletedForm) =>
    run(async () => {
      try {
        await downloadVisitForm(form.public_id);
      } catch {
        throw Error(t("forms.done.downloadFailed"));
      }
    });
  const copyValues = (form: CompletedForm) =>
    run(async () => {
      try {
        await navigator.clipboard.writeText(
          formValuesText(form, {
            checked: t("forms.review.checked"),
          }),
        );
      } catch {
        throw Error(t("forms.done.copyFailed"));
      }
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    });
  const openRecent = (summary: CompletedFormSummary) =>
    run(async () => startReview(await getVisitForm(summary.public_id)));

  const pending =
    step.kind === "review" ? pendingKeys(step.form.entries, review) : [];
  const back =
    step.kind === "fields" || step.kind === "ready" || step.kind === "review"
      ? () => {
          setProblem(null);
          setActiveKey(null);
          setStep({ kind: "choose" });
        }
      : step.kind === "done"
        ? () => startReview({ ...step.form, state: "review" })
        : undefined;

  const problemNotice = problem && step.kind !== "choose" && (
    <InlineNotice data-testid="form-problem" role="alert" tone="error">
      {problem.title}
      {problem.description ? ` ${problem.description}` : ""}
    </InlineNotice>
  );

  let body: JSX.Element;
  let footer: JSX.Element | undefined;
  switch (step.kind) {
    case "choose":
      body = (
        <FormChooser
          busy={busy}
          loading={templates.loading}
          problem={
            problem ??
            (templates.error ? { title: t("forms.errors.load") } : null)
          }
          recent={recent.items}
          starters={templates.starters}
          templates={templates.items}
          onOpenRecent={openRecent}
          onPick={(template) => setStep({ kind: "ready", template })}
          onSetUp={openFields}
          onStarter={(key) =>
            void run(async () =>
              openFields(
                await createFormTemplate({
                  starterKey: key as "work-absence-note",
                }),
              ),
            )
          }
          onUpload={upload}
        />
      );
      break;
    case "fields":
      body = (
        <FormFieldsEditor
          fields={fields}
          name={name}
          onFieldsChange={setFields}
          onNameChange={setName}
        />
      );
      footer = (
        <div className="space-y-2">
          {problemNotice}
          <Button
            className="w-full"
            data-testid="form-save-fields"
            disabled={
              busy || fields.length === 0 || fields.some((f) => !f.label.trim())
            }
            size="lg"
            variant="primary"
            onClick={() => void saveFields(step.template)}
          >
            {busy ? (
              <Loader2 aria-hidden className="animate-spin" />
            ) : (
              <FileCheck2 aria-hidden />
            )}
            {t("forms.fields.save")}
          </Button>
        </div>
      );
      break;
    case "ready": {
      const clinical = step.template.fields.filter(
        (f) => f.role === "clinical",
      ).length;
      const dictated = mode === "dictation";
      body = (
        <div className="space-y-5 px-5 pb-6 pt-1" data-testid="form-ready">
          <div>
            <h2 className="font-v3-display text-[22px] font-bold leading-7 tracking-[-0.01em]">
              {step.template.name}
            </h2>
            <p className="mt-1.5 text-[15px] leading-[22px] text-v3-text-2">
              {t("forms.filling.fieldsToFill", { count: clinical })}
            </p>
          </div>
          <SegmentedControl
            label={t("forms.filling.sourceLabel")}
            segments={[
              {
                value: "visit",
                label: t("forms.filling.sourceVisit"),
                testId: "form-source-visit",
              },
              {
                value: "dictation",
                label: t("forms.filling.sourceDictation"),
                testId: "form-source-dictation",
              },
            ]}
            stretch
            value={mode}
            onValueChange={(next) => {
              setMode(next);
              setTouched(false);
            }}
          />
          {dictated ? (
            <div className="space-y-4" data-testid="form-dictation">
              <div className="min-w-0 space-y-1.5 font-v3">
                <FieldLabel htmlFor={ids.dictation}>
                  {t("forms.filling.dictation")}
                </FieldLabel>
                <DictatedField
                  describedBy={ids.dictationHint}
                  id={ids.dictation}
                  invalid={touched && dictationMissing}
                  maxLength={FORM_DICTATION_MAX}
                  micLabel={{
                    start: t("forms.filling.dictate"),
                    stop: t("dictation.stop"),
                  }}
                  minRows={5}
                  placeholder={t("forms.filling.dictationPlaceholder")}
                  testId="form-dictation-content"
                  value={dictation}
                  onChange={setDictation}
                />
                <p
                  className={cn(
                    "text-[12.5px] leading-[17px] text-v3-text-3",
                    touched && dictationMissing && "text-v3-rec-text",
                  )}
                  id={ids.dictationHint}
                >
                  {touched && dictationMissing
                    ? t("forms.filling.dictationRequired")
                    : t("forms.filling.dictationHint")}
                </p>
              </div>
              <Field
                hint={t("forms.filling.instructionsHint")}
                htmlFor={ids.instructions}
                label={
                  <>
                    {t("forms.filling.instructions")}{" "}
                    <span className="font-normal text-v3-text-3">
                      {t("forms.filling.optional")}
                    </span>
                  </>
                }
              >
                <DictatedField
                  id={ids.instructions}
                  maxLength={FORM_INSTRUCTIONS_MAX}
                  micLabel={{
                    start: t("forms.filling.dictateInstructions"),
                    stop: t("dictation.stop"),
                  }}
                  minRows={2}
                  placeholder={t("forms.filling.instructionsPlaceholder")}
                  testId="form-dictation-instructions"
                  value={instructions}
                  onChange={setInstructions}
                />
              </Field>
            </div>
          ) : null}
          <ul className="space-y-2 text-[14px] leading-5">
            <li
              className="flex gap-3 rounded-[14px] bg-v3-surface-2 px-3.5 py-3"
              data-testid="form-fill-source"
            >
              {dictated ? (
                <Mic
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 text-v3-text-2"
                />
              ) : (
                <Sparkles
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 text-v3-text-2"
                />
              )}
              {dictated
                ? t("forms.filling.dictationOnly")
                : sourceIds.length
                  ? t("forms.filling.sources", { count: sourceIds.length })
                  : t("forms.filling.sourcesNone")}
            </li>
            <li className="flex gap-3 rounded-[14px] bg-v3-surface-2 px-3.5 py-3">
              <Lock
                aria-hidden
                className="mt-0.5 size-4 shrink-0 text-v3-text-2"
              />
              {t("forms.filling.privacy")}
            </li>
          </ul>
          <Button
            className="w-full sm:w-auto"
            data-testid="form-edit-fields"
            variant="ghost"
            onClick={() => openFields(step.template)}
          >
            <Pencil aria-hidden />
            {t("forms.filling.editFields")}
          </Button>
        </div>
      );
      footer = (
        <div className="space-y-2">
          {problemNotice}
          <Button
            className="w-full"
            data-testid="form-fill"
            disabled={busy}
            size="lg"
            variant="primary"
            onClick={() => fill(step.template)}
          >
            <Sparkles aria-hidden />
            {t("forms.filling.fill")}
          </Button>
        </div>
      );
      break;
    }
    case "filling":
      body = (
        <Filling
          body={t("forms.filling.busyBody")}
          dictated={mode === "dictation"}
          title={
            mode === "dictation"
              ? t("forms.filling.titleDictation")
              : t("forms.filling.title")
          }
        />
      );
      break;
    case "review": {
      const form = step.form;
      body = (
        <FormReview
          activeKey={activeKey}
          form={form}
          pending={pending}
          phone={phone}
          review={review}
          onActiveKeyChange={setActiveKey}
          onReviewChange={setReview}
        />
      );
      footer = (
        <div className="space-y-2">
          {problemNotice}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
            {pending.length > 0 ? (
              <Button
                className="w-full sm:w-auto"
                data-testid="form-check-values"
                variant="secondary"
                onClick={() => setActiveKey(pending[0] ?? null)}
              >
                <TriangleAlert aria-hidden className="text-v3-warn-text" />
                {t("forms.review.pending", { count: pending.length })}
              </Button>
            ) : (
              <p className="flex items-center gap-1.5 text-[13.5px] text-v3-ok-text sm:mr-auto">
                <CircleCheck aria-hidden className="size-4" />
                {t("forms.review.allChecked")}
              </p>
            )}
            <Button
              className="w-full sm:ml-auto sm:w-auto"
              data-testid="form-create-pdf"
              disabled={busy || pending.length > 0}
              size="lg"
              variant="primary"
              onClick={() => void complete(form)}
            >
              {busy ? (
                <Loader2 aria-hidden className="animate-spin" />
              ) : (
                <FileCheck2 aria-hidden />
              )}
              {busy ? t("forms.review.creating") : t("forms.review.createPdf")}
            </Button>
          </div>
        </div>
      );
      break;
    }
    case "done":
      body = (
        <div
          className="flex flex-col items-center gap-3 px-6 pb-8 pt-10 text-center"
          data-testid="form-done"
        >
          <span className="flex size-14 items-center justify-center rounded-full bg-v3-ok-tint text-v3-ok-text">
            <CircleCheck aria-hidden className="size-7" />
          </span>
          <h2 className="font-v3-display text-[22px] font-bold leading-7">
            {t("forms.done.heading")}
          </h2>
          <p className="max-w-sm text-[15px] leading-[22px] text-v3-text-2">
            {t("forms.done.description", { name: step.form.name })}
          </p>
          <p className="max-w-sm text-[13.5px] leading-5 text-v3-text-3">
            {t("forms.done.checkPdf")}
          </p>
          {problemNotice}
        </div>
      );
      footer = (
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <Button
            className="w-full sm:w-auto"
            data-testid="form-download"
            disabled={busy}
            size="lg"
            variant="primary"
            onClick={() => void download(step.form)}
          >
            <Download aria-hidden />
            {t("forms.done.download")}
          </Button>
          <Button
            className="w-full sm:w-auto"
            data-testid="form-copy-values"
            disabled={busy}
            size="lg"
            variant="secondary"
            onClick={() => void copyValues(step.form)}
          >
            {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
            {copied ? t("forms.done.copied") : t("forms.done.copy")}
          </Button>
          <Button
            className="w-full sm:mr-auto sm:w-auto"
            data-testid="form-edit-values"
            size="lg"
            variant="ghost"
            onClick={() => startReview({ ...step.form, state: "review" })}
          >
            <Pencil aria-hidden />
            {t("forms.done.edit")}
          </Button>
        </div>
      );
      break;
  }

  return (
    <V3Drawer
      disablePointerDismissal={busy}
      footer={footer}
      open={open}
      testId="form-sheet"
      title={t("forms.title")}
      tools={
        back && (
          <Button
            data-testid="form-back"
            disabled={busy}
            size="default"
            variant="ghost"
            onClick={back}
          >
            <ArrowLeft aria-hidden />
            <span className="max-sm:sr-only">{t("forms.back")}</span>
          </Button>
        )
      }
      width="wide"
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
    >
      {body}
    </V3Drawer>
  );
};
